/**
 * `/workspace/:id/regelwerk/:kanton/:gemeinde` — one address, two packs.
 *
 * A pack key is either a workspace-owned DRAFT (then the verification queue is
 * the page) or a COMMITTED pack read out of `rulebase/` (then the read-only
 * detail is). Until now the route rendered the verification screen
 * unconditionally, so the four committed Gemeinden answered «Entwurfs-Pack
 * nicht gefunden» — which is why the overview stopped linking them at all.
 *
 * The decision is made from the register (`GET /gemeinden`), not from a guess:
 * the same call the overview makes, with the same `draft` flag the rest of the
 * product reads (features/rulebase/rulebase.ts `isDraft`). A pack key the
 * register does not know falls through to the verification screen, whose own
 * 404 is then the honest answer — this component never invents one.
 */
import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { LoadingBlock } from '@/components/LoadingBlock'
import { useI18n } from '@/i18n'
import { useAsync } from '@/lib/useAsync'

import { CommittedPackDetail } from './CommittedPackDetail'
import { VerifyPackScreen } from './VerifyPackScreen'
import { isDraft, joinPackId, packIdOf } from './rulebase'
import type { GemeindePack, WorkspaceRef } from '@/api/types'

export function PackScreen({
  workspace,
  kanton,
  gemeinde,
}: {
  workspace: WorkspaceRef
  kanton: string
  gemeinde: string
}) {
  const { t } = useI18n()
  const packId = joinPackId(kanton.toLowerCase(), gemeinde.toLowerCase())
  const packs = useAsync<GemeindePack[]>(
    (signal) => api.gemeinden(workspace.id, signal),
    [workspace.id],
  )

  if (packs.loading) return <LoadingBlock />
  if (packs.error) {
    return (
      <ErrorNote message={packs.error} onRetry={packs.reload} retryLabel={t('common.retry')} />
    )
  }

  const pack = (packs.data ?? []).find(
    (candidate) => packIdOf(candidate).toLowerCase() === packId,
  )

  if (pack && !isDraft(pack)) {
    return <CommittedPackDetail pack={pack} workspaceId={workspace.id} />
  }

  return <VerifyPackScreen workspace={workspace} kanton={kanton} gemeinde={gemeinde} />
}
